import { FabricImage, FabricObject, Group } from 'fabric';
import {
  readGroupCollection,
  arrangeGroup,
  type GroupCollectionConfig
} from '@doer/frontend-components/creativity-editor';
import { resolveValuePath } from '../core/value-path';
import type { CanvasJSONObject, Effect } from './effect';

type GroupJsonNode = CanvasJSONObject & {
  groupCollection?: GroupCollectionConfig;
  objects?: CanvasJSONObject[];
};

function isGroupPiece(obj: FabricObject): obj is Group {
  return obj.type === 'group' && typeof (obj as Group).getObjects === 'function';
}

function isImageSlot(obj: FabricObject): obj is FabricImage {
  return obj.type === 'image' && typeof (obj as FabricImage).setSrc === 'function';
}

function extractUrls(value: unknown, urlPath?: string): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(item => resolveValuePath(item, urlPath))
    .filter((url): url is string => typeof url === 'string' && url.length > 0);
}

export const collectionEffect: Effect = {
  applyToJson(obj, value) {
    const node = obj as GroupJsonNode;
    const cfg = readGroupCollection(node);
    if (!cfg) return null;

    const urls = extractUrls(value, cfg.urlPath);
    const slots = node.objects ?? [];

    const updatedSlots = slots.map((slot, i) => {
      if (i < urls.length) {
        return { ...slot, src: urls[i], crossOrigin: 'anonymous', visible: true };
      }
      return { ...slot, visible: false };
    });

    return { ...node, objects: updatedSlots };
  },

  // setSrc adopts the native image dimensions arrangeGroup needs, but reloading an already-loaded
  // slot flashes the image, so skip it when the src is unchanged (dimensions are already present).
  async applyToCanvas(obj: FabricObject, value) {
    if (!isGroupPiece(obj)) return;
    const cfg = readGroupCollection(obj);
    if (!cfg) return;

    const urls = extractUrls(value, cfg.urlPath);
    const slots = obj.getObjects();

    for (let i = 0; i < slots.length; i++) {
      const slot = slots[i];
      if (!isImageSlot(slot)) continue;

      if (i < urls.length) {
        slot.set({ visible: true });
        if (slot.getSrc() !== urls[i]) await slot.setSrc(urls[i], { crossOrigin: 'anonymous' });
      } else {
        slot.set({ visible: false });
      }
    }

    arrangeGroup(obj);
  }
};
